/**
 * Login Page
 *
 * Immersive game-themed login experience with pixel-art elements.
 * User login interface with email/password authentication.
 */

import { useState, FormEvent, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { PageAtmosphere } from '../components/atmosphere/PageAtmosphere';
import '../styles/atmosphere/pages/create.css';
import styles from './AuthPages.module.css';

/**
 * Floating pixel decoration component
 */
function PixelDecoration() {
  return (
    <div className={styles.pixelDecoration}>
      <div className={styles.pixel}></div>
      <div className={styles.pixel}></div>
      <div className={styles.pixel}></div>
      <div className={styles.pixel}></div>
      <div className={styles.pixel}></div>
      <div className={styles.pixel}></div>
      <div className={styles.pixel}></div>
      <div className={styles.pixel}></div>
    </div>
  );
}

/**
 * Cyber pool with emerging creature animation
 */
function CyberPoolAnimation() {
  return (
    <div className={styles.cyberPoolContainer}>
      {/* Glowing pool base */}
      <div className={styles.cyberPool} />

      {/* White rising pixels */}
      <div className={styles.risingPixels}>
        <div className={styles.whitePixel} />
        <div className={styles.whitePixel} />
        <div className={styles.whitePixel} />
        <div className={styles.whitePixel} />
        <div className={styles.whitePixel} />
        <div className={styles.whitePixel} />
        <div className={styles.whitePixel} />
        <div className={styles.whitePixel} />
        <div className={styles.whitePixel} />
        <div className={styles.whitePixel} />
        <div className={styles.whitePixel} />
        <div className={styles.whitePixel} />
        <div className={styles.whitePixel} />
        <div className={styles.whitePixel} />
        <div className={styles.whitePixel} />
        <div className={styles.whitePixel} />
        <div className={styles.whitePixel} />
        <div className={styles.whitePixel} />
        <div className={styles.whitePixel} />
        <div className={styles.whitePixel} />
        <div className={styles.whitePixel} />
        <div className={styles.whitePixel} />
        <div className={styles.whitePixel} />
        <div className={styles.whitePixel} />
      </div>

      {/* Emerging creature image */}
      <img
        src="/assets/UI/emergencetheme.jpeg"
        alt="Emerging creature"
        className={styles.emergingCreature}
      />
    </div>
  );
}


/**
 * Login Page Component
 */
export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { signIn, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Get redirect URL from query params, validated to prevent open redirects
  const searchParams = new URLSearchParams(location.search);
  const rawRedirect = searchParams.get('redirect') || '/home';
  const redirectUrl = rawRedirect.startsWith('/') && !rawRedirect.includes('//') ? rawRedirect : '/home';

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      navigate(redirectUrl, { replace: true });
    }
  }, [user, navigate, redirectUrl]);

  /**
   * Validate email format
   */
  function isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Handle form submission
   */
  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');

    // Validation
    if (!email.trim()) {
      setError('Email is required');
      return;
    }

    if (!isValidEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    if (!password) {
      setError('Password is required');
      return;
    }

    // Attempt login
    setLoading(true);

    try {
      await signIn(email, password);
      // Mark that user has an account for future visits
      localStorage.setItem('has_account', 'true');
      // Navigation happens in useEffect when user state updates
    } catch (err: any) {
      // Handle Firebase auth errors
      if (err.code === 'auth/wrong-password' || err.code === 'auth/user-not-found') {
        setError('Invalid email or password');
      } else if (err.code === 'auth/too-many-requests') {
        setError('Too many failed attempts. Please try again later.');
      } else {
        setError('Authentication failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <PageAtmosphere page="login">
    <div className={styles.pageContainer}>
      {/* Floating pixel decorations */}
      <PixelDecoration />

      {/* Top - Banner with logo */}
      <div className={styles.bannerSection}>
        <img
          src="/assets/branding/logo-full.jpeg"
          alt="Drawn of War"
          className={styles.bannerLogo}
        />
      </div>

      {/* Main content - Login on left, Image on right */}
      <div className={styles.mainContent}>
        {/* Left - Login form */}
        <div className={styles.formSection}>
          <div className={styles.authCard}>
            {/* Page title */}
            <h1 className={styles.pageTitle}>Welcome Back</h1>

            {/* Login form */}
            <form onSubmit={handleSubmit} className={styles.form}>
              {/* Error message */}
              {error && (
                <div className={styles.errorMessage} role="alert">
                  {error}
                </div>
              )}

              {/* Email field */}
              <div className={styles.formGroup}>
                <label htmlFor="email" className={styles.label}>
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  placeholder="warrior@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                  className={styles.input}
                />
              </div>

              {/* Password field */}
              <div className={styles.formGroup}>
                <label htmlFor="password" className={styles.label}>
                  Password
                </label>
                <div className={styles.passwordWrapper}>
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={loading}
                    className={styles.input}
                  />
                  <button
                    type="button"
                    className={styles.passwordToggle}
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                        <line x1="1" y1="1" x2="23" y2="23" />
                      </svg>
                    ) : (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              {/* Submit button */}
              <button
                type="submit"
                disabled={loading}
                className={`${styles.submitButton} ${loading ? styles.loading : ''}`}
              >
                {loading ? 'Entering the Arena...' : 'Enter the Arena'}
              </button>
            </form>

            {/* Footer link */}
            <div className={styles.footer}>
              <p className={styles.footerText}>
                Want to join the beta?{' '}
                <Link to="/welcome" className={styles.footerLink}>
                  Request Access
                </Link>
              </p>
              <p className={styles.footerText} style={{ marginTop: '0.5rem', fontSize: '0.75rem' }}>
                Already approved?{' '}
                <Link to="/register" className={styles.footerLink}>
                  Create Account
                </Link>
              </p>
            </div>
          </div>
        </div>

        {/* Right - Large emerging creature image with rising pixels */}
        <div className={styles.imageSection}>
          <img
            src="/assets/UI/emergencetheme.jpeg"
            alt="Emerging creature"
            className={styles.bottomImage}
          />
          {/* Rising white pixels */}
          <div className={styles.risingPixelsBottom}>
            <div className={styles.whitePixel} />
            <div className={styles.whitePixel} />
            <div className={styles.whitePixel} />
            <div className={styles.whitePixel} />
            <div className={styles.whitePixel} />
            <div className={styles.whitePixel} />
            <div className={styles.whitePixel} />
            <div className={styles.whitePixel} />
            <div className={styles.whitePixel} />
            <div className={styles.whitePixel} />
            <div className={styles.whitePixel} />
            <div className={styles.whitePixel} />
            <div className={styles.whitePixel} />
            <div className={styles.whitePixel} />
            <div className={styles.whitePixel} />
            <div className={styles.whitePixel} />
            <div className={styles.whitePixel} />
            <div className={styles.whitePixel} />
            <div className={styles.whitePixel} />
            <div className={styles.whitePixel} />
            <div className={styles.whitePixel} />
            <div className={styles.whitePixel} />
            <div className={styles.whitePixel} />
            <div className={styles.whitePixel} />
            <div className={styles.whitePixel} />
            <div className={styles.whitePixel} />
            <div className={styles.whitePixel} />
            <div className={styles.whitePixel} />
            <div className={styles.whitePixel} />
            <div className={styles.whitePixel} />
          </div>
        </div>
      </div>
    </div>
    </PageAtmosphere>
  );
}
